import { useSelector } from "react-redux";
import useDocumentTitle from "../../util/useDocumentTitle";
import { selectCurrentUser } from "../../redux/authentication/authSlice";
import { UserGroup } from "../../common/User";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  EvaluationInsightView,
  EvaluationStatus,
} from "../../common/Evaluation";
import {
  Autocomplete,
  TextField,
  Button as MaterialButton,
  Table,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Fleet } from "../../common/Fleet";
import { useGetFleetsQuery } from "../../redux/api/fleet";
import {
  useCreateEvaluationFromVesselAndProfileMutation,
  useCreateMissingEvaluationsFromInsightsMutation,
  useGetEvaluationInsightsQuery,
  useGetEvaluationMetadataQuery,
} from "../../redux/api/evaluation";
import { toast } from "react-toastify";
import "./EvaluationInsights.scss";
import EvaluationInsightsTable from "./EvaluationInsightsTable";
import { Vessel } from "../../common/Vessel";
import { MarinerProfile } from "../../common/MarinerProfile";

const EvaluationInsights = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useSelector(selectCurrentUser);
  const userGroup = user?.userGroup as UserGroup;
  const isHumanResourcesOrCrewing =
    userGroup === UserGroup.HUMAN_RESOURCES ||
    userGroup === UserGroup.HUMAN_RESOURCES_ADMINS ||
    userGroup === UserGroup.CREWING_EVAL_SPECIALISTS ||
    userGroup === UserGroup.CREWING_MANAGERS;
  const [creatingEvaluations, setCreatingEvaluations] = useState(false);
  const [fleet, setFleet] = useState<Fleet | null>(null);
  const [skipQuery, setSkipQuery] = useState(fleet == null);
  const [vessel, setVessel] = useState<Vessel | null>(null);

  useDocumentTitle(
    isHumanResourcesOrCrewing
      ? "Performance Evaluation Insights"
      : "Performance Evaluations"
  );

  const { data: fleets, isLoading: isFleetsLoading } = useGetFleetsQuery(
    {},
    {
      refetchOnMountOrArgChange: true,
    }
  );

  // skip these queries if fleet is null
  const {
    data: metadata,
    isLoading: isMetadataLoading,
    refetch: refetchMetadata,
  } = useGetEvaluationMetadataQuery(
    {
      fleetId: fleet?.id,
      vesselId: vessel?.id,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: skipQuery,
    }
  );

  // This api call should get a list of mariners that are onboard the fleets with the desired positions
  const {
    data: evaluationInsights,
    isLoading: isEvaluationInsightsLoading,
    refetch: refetchInsights,
  } = useGetEvaluationInsightsQuery(
    {
      fleetId: fleet?.id,
      vesselId: vessel?.id,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: skipQuery,
    }
  );

  useEffect(() => {
    if (!isFleetsLoading && fleets !== undefined) {
      if (fleets.length === 1) {
        setFleet(fleets[0]);
        setSkipQuery(false);
      } else if (searchParams.get("fleet")) {
        const searchFleet =
          fleets.find((f) => f.name === searchParams.get("fleet")) || null;

        if (searchFleet) {
          setFleet(searchFleet);
          setSkipQuery(false);
        }
      }
    }
  }, [isFleetsLoading, fleets, searchParams]);

  const [createEvaluationFromVesselAndProfileMutation] =
    useCreateEvaluationFromVesselAndProfileMutation();
  const [createMissingEvaluations] =
    useCreateMissingEvaluationsFromInsightsMutation();

  const handleClick = async (
    evaluationId: number,
    vessel: Vessel,
    profile: MarinerProfile
  ) => {
    if (creatingEvaluations) {
      return;
    }

    setCreatingEvaluations(true);
    if (evaluationId != null) {
      navigate(`/evaluations/${evaluationId}`);
      return;
    }
    processMarinerEvaluation(vessel, profile);
    return;
  };

  const handleCreateEvaluations = async () => {
    setCreatingEvaluations(true);
    const result = await createMissingEvaluations({
      fleetId: fleet?.id,
      vesselId: vessel?.id,
    });
    if ("error" in result) {
      setCreatingEvaluations(false);
      return;
    }
    refetchMetadata();
    refetchInsights();
    setCreatingEvaluations(false);
    toast.success(<p>Evaluations created successfully.</p>, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
  };

  const getReminderEmail = (insight: EvaluationInsightView) => {
    switch (insight.status) {
      case EvaluationStatus.EVALUATION_INITIATED:
      case EvaluationStatus.ASSESSOR_1_EDIT:
      case EvaluationStatus.ASSESSOR_2_RETURN:
        return insight.assessor1Email;
      case EvaluationStatus.ASSESSOR_1_SUBMIT:
      case EvaluationStatus.FINAL_REVIEWER_RETURN:
        return insight.assessor2Email;
      case EvaluationStatus.ASSESSOR_2_APPROVE:
        return insight.finalReviewerEmail;
      case EvaluationStatus.FINAL_REVIEWER_APPROVE:
        return insight.profile.email;
      default:
        return "";
    }
  };

  const handleEmailCallBack = () => {
    toast.success(<p>Email sent successfully.</p>, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
  };

  async function processMarinerEvaluation(
    vessel: Vessel,
    profile: MarinerProfile
  ) {
    const vesselId = vessel.id;
    const profileId = profile.id;
    if (!vesselId || !profileId) {
      // TODO: error handling
      return;
    }
    const createResult = await createEvaluationFromVesselAndProfileMutation({
      vesselId,
      profileId,
    });
    if ("error" in createResult) {
      return;
    }
    navigate(`/evaluations/${createResult.data.id}`);
    return;
  }

  // helper function to check if any Vessels in a Fleet are in ROS
  const hasRos = (fleet: Fleet | null | undefined) => {
    if (!fleet || fleet.name === "MSC") {
      return false;
    }
    return fleet.vessels.some((v) => v.ros === true);
  };

  return (
    <>
      <div className="flex-col wd-100">
        <div className="flex align-items-center justify-content-btwn mb-1 eval-insights-sticky eval-header">
          <h1>
            {isHumanResourcesOrCrewing
              ? "Performance Evaluation Insights"
              : "Performance Evaluations"}
          </h1>
        </div>
        <div className="eval-insights-sticky eval-fleet">
          {!isFleetsLoading && (
            <>
              <div className="flex align-items-center g-3">
                <label className="large-filter mb-2" style={{ width: "250px" }}>
                  <span>Fleet:</span>
                  <Autocomplete
                    className="fleet-dropdown"
                    // ARC shouldn't be available in fleet dropdown
                    options={
                      fleets
                        ? (fleets as Fleet[]).filter(
                            (fleet) => fleet.name !== "ARC"
                          )
                        : []
                    }
                    value={fleet}
                    onChange={(_, value) => {
                      if (value != null) {
                        setFleet(value);
                        setVessel(null);
                        setSkipQuery(false);
                        navigate("/evaluations?fleet=" + value.name, {
                          replace: true,
                        });
                      }
                    }}
                    renderInput={(params) => <TextField {...params} />}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    getOptionLabel={(opt) => (opt.name ? opt.name : "")}
                  />
                </label>
                {!!fleet && (
                  <>
                    <label className="large-filter mb-2" style={{ width: "250px" }}>
                      <span>Vessel:</span>
                      <Autocomplete
                        className="vessel-dropdown"
                        options={fleet?.vessels || []}
                        value={vessel}
                        onChange={(_, value) => {
                          setVessel(value);
                          setSkipQuery(false);
                        }}
                        renderInput={(params) => <TextField {...params} />}
                        isOptionEqualToValue={(opt, val) => opt.id === val.id}
                        getOptionLabel={(opt) => (opt.name ? opt.name : "")}
                      />
                    </label>
                    <div className="ml-2">
                      <MaterialButton
                        className="blue-button my-1 wd-auto"
                        disabled={creatingEvaluations}
                        onClick={handleCreateEvaluations}
                      >
                        Initiate Bulk Evaluations
                      </MaterialButton>
                    </div>
                  </>
                )}
              </div>
            </>
          )}{" "}
        </div>

        {metadata && !isMetadataLoading && (
          <div className="flex g-4 responsive-flex">
            <div className="statistic p-2 flex-col justify-content-center gr-0 bg-lightblue">
              <h4>Senior Officer Evaluations</h4>
              {isHumanResourcesOrCrewing && (
                <div className="vessel-heading flex g-1 justify-content-btwn wd-100">
                  <div className="flex g-1">
                    <div className="block ht-1px wd-1px bg-red"></div>
                    <h4>Not Started</h4>
                  </div>
                  <div className="vessel-subheading wd-auto">
                    <h5 className="color-navy b p-6 m-6">
                      {metadata?.seniorNotStartedCount}
                    </h5>
                  </div>
                </div>
              )}
              <div className="vessel-heading flex g-1 justify-content-btwn wd-100">
                <div className="flex g-1">
                  <div className="block ht-1px wd-1px bg-orange"></div>
                  <h4>In Progress</h4>
                </div>
                <div className="vessel-subheading wd-auto">
                  <h5 className="color-navy b p-6 m-6">
                    {metadata?.seniorInProgressCount}
                  </h5>
                </div>
              </div>
              {isHumanResourcesOrCrewing && (
                <div className="vessel-heading flex g-1 justify-content-btwn wd-100">
                  <div className="flex g-1">
                    <div className="block ht-1px wd-1px bg-green"></div>
                    <h4>Completed</h4>
                  </div>
                  <div className="vessel-subheading wd-auto">
                    <h5 className="color-navy b p-6 m-6">
                      {metadata?.seniorCompletedCount}
                    </h5>
                  </div>
                </div>
              )}
              <h4 className="pt-2">Junior Officer Evaluations</h4>
              <div className="vessel-heading flex g-1 justify-content-btwn wd-100">
                <div className="flex g-1">
                  <div className="block ht-1px wd-1px bg-orange"></div>
                  <h4>In Progress</h4>
                </div>
                <div className="vessel-subheading wd-auto">
                  <h5 className="color-navy b p-6 m-6">
                    {metadata?.juniorEvaluationCount}
                  </h5>
                </div>
              </div>
              <h4 className="pt-2">Unlicensed Evaluations</h4>
              <div className="vessel-heading flex g-1 justify-content-btwn wd-100">
                <div className="flex g-1">
                  <div className="block ht-1px wd-1px bg-orange"></div>
                  <h4>In Progress</h4>
                </div>
                <div className="vessel-subheading wd-auto">
                  <h5 className="color-navy b p-6 m-6">
                    {metadata?.unlicensedEvaluationCount}
                  </h5>
                </div>
              </div>
            </div>
            {fleet?.name === "TORTUGA" ? (
              <div>
                <Table className="matrix-table">
                  <TableHead>
                    <TableCell colSpan={4} align="center">
                      <h4>Review Matrix</h4>
                    </TableCell>
                  </TableHead>

                  <TableHead>
                    <TableRow>
                      <TableCell>Review</TableCell>
                      <TableCell>1st Assessor</TableCell>
                      <TableCell>2nd Assessor</TableCell>
                      <TableCell>Reviewer</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell className="b-600">Master</TableCell>
                      <TableCell>Director of LNG Ops</TableCell>
                      <TableCell>VP</TableCell>
                      <TableCell>President</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="b-600">Chief Engineer</TableCell>
                      <TableCell>Master</TableCell>
                      <TableCell>Director of LNG Ops</TableCell>
                      <TableCell>VP</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="b-600">Mate</TableCell>
                      <TableCell>Master</TableCell>
                      <TableCell>Director of LNG Ops</TableCell>
                      <TableCell>VP</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="b-600">1st AE</TableCell>
                      <TableCell>Master</TableCell>
                      <TableCell>Director of LNG Ops</TableCell>
                      <TableCell>VP</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div>
                <Table className="matrix-table">
                  <TableHead>
                    <TableCell colSpan={4} align="center">
                      <h4>Review Matrix</h4>
                    </TableCell>
                  </TableHead>

                  <TableHead>
                    <TableRow>
                      <TableCell>Review</TableCell>
                      <TableCell>1st Assessor</TableCell>
                      <TableCell>2nd Assessor</TableCell>
                      <TableCell>Reviewer</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell className="b-600">Master</TableCell>
                      <TableCell>DSM</TableCell>
                      <TableCell>VP</TableCell>
                      <TableCell>President</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="b-600">Chief</TableCell>
                      <TableCell>Master</TableCell>
                      <TableCell>DSM</TableCell>
                      <TableCell>VP</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="b-600">CM</TableCell>
                      <TableCell>Master</TableCell>
                      <TableCell>DSM</TableCell>
                      <TableCell>VP</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="b-600">1st AE</TableCell>
                      <TableCell>Chief Engineer</TableCell>
                      <TableCell>Master</TableCell>
                      <TableCell>DSM</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}
            {hasRos(fleet) && (
              <div>
                <Table className="matrix-table">
                  <TableHead>
                    <TableCell colSpan={4} align="center">
                      <h4>ROS Review Matrix</h4>
                    </TableCell>
                  </TableHead>
                  <TableHead></TableHead>
                  <TableHead>
                    <TableRow>
                      <TableCell>Review</TableCell>
                      <TableCell>1st Assessor</TableCell>
                      <TableCell>2nd Assessor</TableCell>
                      <TableCell>Reviewer</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell className="b-600">Chief</TableCell>
                      <TableCell>Port Engineer</TableCell>
                      <TableCell>DSM</TableCell>
                      <TableCell>VP</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="b-600">CM</TableCell>
                      <TableCell>Port Engineer</TableCell>
                      <TableCell>DSM</TableCell>
                      <TableCell>VP</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="b-600">1st AE</TableCell>
                      <TableCell>Chief Engineer</TableCell>
                      <TableCell>Port Engineer</TableCell>
                      <TableCell>DSM</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
        {!!evaluationInsights && !isEvaluationInsightsLoading != null && (
          <>
            <div style={{ marginTop: "2%" }}>
              <h2 style={{ color: "black" }}>Evaluations Needed</h2>
            </div>
            <h2 style={{ marginTop: "2%" }}>Senior Officer Evaluations</h2>

            <EvaluationInsightsTable
              evalList={evaluationInsights.seniorOfficerEvaluations}
              getReminderEmail={getReminderEmail}
              emailCallback={handleEmailCallBack}
              handleClick={handleClick}
            />

            <h2 style={{ marginTop: "2%" }}>Junior Officer Evaluations</h2>

            <EvaluationInsightsTable
              evalList={evaluationInsights.juniorOfficerEvaluations}
              getReminderEmail={getReminderEmail}
              emailCallback={handleEmailCallBack}
              handleClick={handleClick}
            />

            <h2 style={{ marginTop: "2%" }}>Unlicensed Evaluations</h2>

            <EvaluationInsightsTable
              evalList={evaluationInsights.unlicensedEvaluations}
              getReminderEmail={getReminderEmail}
              emailCallback={handleEmailCallBack}
              handleClick={handleClick}
            />
          </>
        )}
      </div>
    </>
  );
};

export default EvaluationInsights;
